if (!customElements.get('video-product-card')) {
    customElements.define(
        'video-product-card',
        class VideoProductCard extends HTMLElement {
            constructor() {
                super();
                this.variantSelector = this.querySelector('variant-selects');
                this._boundHandleInputChange = this.handleInputChange.bind(this);
            }

            connectedCallback() {
                // console.group(`🎯 video-product-card 连接: ${this.dataset.targetUrl}`);
                // console.log('区块ID:', this.dataset.block);

                // 使用更具体的选择器，确保只在当前组件内查找
                this.variantSelector = this.querySelector(':scope > variant-selects');

                if (!this.variantSelector) {
                    // console.warn('⚠️ variantSelector 未找到，尝试其他选择器...');
                    // 尝试其他可能的选择器
                    this.variantSelector = this.querySelector('variant-selects');
                }

                if (!this.variantSelector) {
                    // console.warn('⚠️ variantSelector 未找到，跳过初始化');
                    // console.groupEnd();
                    return;
                }

                // console.log('✅ 找到 variantSelector:', this.variantSelector);

                const fieldset = this.variantSelector?.querySelector('fieldset');
                if (!fieldset) return;

                const inputs = fieldset.querySelectorAll('input');
                if (!inputs.length) return;

                const firstInput = inputs[0];
                firstInput.click();

                inputs.forEach(input => {
                    input.removeEventListener('change', this._boundHandleInputChange);
                    input.addEventListener('change', this._boundHandleInputChange);
                });

                // console.log('✅ 组件初始化完成');
                // console.groupEnd();
            }

            get selectedOptionValues() {
                return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
                    ({ dataset }) => dataset.optionValueId
                );
            }

            handleInputChange(event) {
                // 如果只想在选中时才执行，可以加判断：
                // if (!event.target.checked) return;

                // console.group(`📝 输入变化 - 组件: ${this.dataset.targetUrl}`);
                // console.log('事件目标:', event.target);
                // console.log('当前值:', event.target.value);
                // console.log('当前组件标识:', this.dataset.block);
                this.handleOptionValueChange({
                    data: {
                        event,
                        target: event.target,
                        selectedOptionValues: this.selectedOptionValues
                    }
                });

                // console.groupEnd();
            }

            handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
                // 检查事件目标是否在组件内
                if (!this.contains(event.target)) {
                    return;
                }

                // 重置产品表单状态
                this.resetProductFormState();

                // 确定产品URL
                const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.targetUrl;
                this.pendingRequestUrl = productUrl;

                // 判断是否需要切换产品
                const shouldSwapProduct = this.dataset.url !== productUrl;

                // 判断是否需要获取完整页面
                const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

                // 构建请求URL
                const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

                // 确定回调函数
                const callback = this.handleUpdateProductInfo(productUrl);

                // 渲染产品信息
                this.renderProductInfo({
                    requestUrl: requestUrl,
                    targetId: target.id,
                    callback: callback
                });
            }

            handleUpdateProductInfo() {
                return (html) => {
                    const variant = this.getSelectedVariant(html);
                    this.updateVariantInputs(variant?.id);

                    if (!variant) {
                        this.setUnavailable();
                        return;
                    }

                    // 更新价格
                    const compareAtPrice = this.querySelector('.video-product-card__product__price__compare-at');
                    if (compareAtPrice) compareAtPrice.innerHTML = this.currencyFormatter(variant?.compare_at_price);

                    const regularPriceNow = this.querySelector('.video-product-card__product__price__price');
                    if (regularPriceNow) regularPriceNow.innerHTML = this.currencyFormatter(variant?.price);

                    // 更新变体图
                    const media = this.querySelector('.video-product-card__product__media-image');
                    if (media && variant?.featured_image?.src) media.srcset = variant?.featured_image.src;

                    this.productForm?.toggleSubmitButton(
                        html.querySelector('product-form .product-form__submit')?.hasAttribute('disabled') ?? true,
                        window.variantStrings.soldOut
                    );

                    publish(PUB_SUB_EVENTS.variantChange, {
                        data: {
                            sectionId: this.sectionId,
                            html,
                            variant,
                        },
                    });
                };
            }

            setUnavailable() {
                this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

                const selectors = ['price', 'media', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
                    .map((id) => `#${id}-${this.dataset.block}`)
                    .join(', ');
                this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
            }

            updateVariantInputs(variantId) {
                this.querySelectorAll(
                    `#product-form-${this.dataset.block}, #product-form-installment-${this.dataset.block}`
                ).forEach((productForm) => {
                    const input = productForm.querySelector('input[name="id"]');
                    input.value = variantId ?? '';
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                });
            }

            renderProductInfo({ requestUrl, targetId, callback }) {
                this.abortController?.abort();
                this.abortController = new AbortController();

                fetch(requestUrl, { signal: this.abortController.signal })
                    .then((response) => response.text())
                    .then((responseText) => {
                        this.pendingRequestUrl = null;
                        const html = new DOMParser().parseFromString(responseText, 'text/html');
                        callback(html);
                    })
                    .catch((error) => {
                        if (error.name !== 'AbortError') {
                            console.error(error);
                        }
                    });
            }

            resetProductFormState() {
                const productForm = this.productForm;
                productForm?.toggleSubmitButton(true);
                productForm?.handleErrorMessage();
            }

            buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
                const params = [];
                !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
                if (optionValues.length) {
                    params.push(`option_values=${optionValues.join(',')}`);
                }
                return `${url}?${params.join('&')}`;
            }

            getSelectedVariant(productInfoNode) {
                const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
                return !!selectedVariant ? JSON.parse(selectedVariant) : null;
            }

            currencyFormatter(amount) {
                const formatTemplate = window.moneyFormat || '${{amount}}';
                if (amount === null || typeof amount === 'undefined') {
                    return '';
                }
                // Shopify money format expects amount in cents.
                const amountInCents = typeof amount === 'string' ? String(amount).replace('.', '') : amount;

                function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
                    if (isNaN(number) || number == null) {
                        return '';
                    }
                    number = (number / 100.0).toFixed(precision);
                    const parts = number.split('.');
                    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
                    return dollars + (parts[1] ? decimal + parts[1] : '');
                }

                let value;
                const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
                const formatMatch = formatTemplate.match(placeholderRegex);

                if (!formatMatch) {
                    return formatTemplate;
                }

                switch (formatMatch[1]) {
                    case 'amount':
                        value = formatWithDelimiters(amountInCents, 2);
                        break;
                    case 'amount_no_decimals':
                        value = formatWithDelimiters(amountInCents, 0);
                        break;
                    case 'amount_with_comma_separator':
                        value = formatWithDelimiters(amountInCents, 2, '.', ',');
                        break;
                    case 'amount_no_decimals_with_comma_separator':
                        value = formatWithDelimiters(amountInCents, 0, '.', ',');
                        break;
                    default:
                        value = formatWithDelimiters(amountInCents, 2);
                        break;
                }

                return formatTemplate.replace(placeholderRegex, value);
            }

            get productForm() {
                return this.querySelector(`product-form`);
            }
        }
    );
}
